import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShoppingCart, Printer } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/Common";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const nome = (c) => c.denominazione || `${c.nome} ${c.cognome}`;

const saveBlob = (r) => {
  const cd = r.headers["content-disposition"] || "";
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = (cd.match(/filename="?([^";]+)"?/) || [])[1] || "ordine.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const OrdineFornitore = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [forn, setForn] = useState([]);
  const [fid, setFid] = useState("");
  const [righe, setRighe] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/customers").then((r) => setForn(r.data.filter((c) => ["fornitore", "entrambi"].includes(c.tipo))));
    api.get("/magazzino/sottoscorta").then((r) => setRighe(r.data.map((x) => ({ ...x, costo: x.costo_unitario || x.ultimo_costo || 0, quantita: Math.max(0, +(x.scorta_minima - x.giacenza).toFixed(3)) }))));
  }, [open]);

  const print = async () => {
    setBusy(true);
    try {
      const r = await api.post("/magazzino/ordine.pdf", { fornitore_id: fid, note, righe: righe.map((x) => ({ product_id: x.product_id, quantita: Number(x.quantita || 0), costo: Number(x.costo || 0) })) }, { responseType: "blob" });
      saveBlob(r);
      toast.success("Ordine creato");
      setOpen(false);
      onDone?.();
    } catch (e) {
      let msg = errMsg(e);
      try { msg = JSON.parse(await e.response.data.text()).detail; } catch { /* keep msg */ }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="outline" className="bg-white" data-testid="supplier-order-button" onClick={() => setOpen(true)}><ShoppingCart className="mr-2 h-4 w-4" />Ordine al fornitore</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="supplier-order-dialog">
          <DialogHeader><DialogTitle>Ordine al fornitore</DialogTitle></DialogHeader>
          <Field label="Fornitore">
            <Select value={fid || undefined} onValueChange={setFid}>
              <SelectTrigger data-testid="supplier-order-supplier-select"><SelectValue placeholder={forn.length ? "Seleziona…" : "Nessun fornitore in anagrafica"} /></SelectTrigger>
              <SelectContent>{forn.map((c) => <SelectItem key={c.id} value={c.id}>{nome(c)}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {righe.length === 0 ? <p data-testid="supplier-order-empty" className="text-sm text-slate-600">Nessun articolo sotto la scorta minima.</p> : (
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-2 text-[11px] font-semibold uppercase text-slate-600"><span className="col-span-5">Articolo sotto scorta</span><span className="col-span-2 text-right">Giacenza</span><span className="col-span-2">Da ordinare</span><span className="col-span-3">Prezzo ind. €</span></div>
              {righe.map((x, i) => (
                <div key={x.product_id} className="grid grid-cols-12 items-center gap-2 text-sm" data-testid={`supplier-order-row-${i}`}>
                  <span className="col-span-5 truncate">{x.codice && <span className="font-mono text-xs">{x.codice} · </span>}{x.descrizione}</span>
                  <span className="col-span-2 text-right font-mono">{x.giacenza.toLocaleString("it-IT")}</span>
                  <Input className="col-span-2 font-mono" type="number" min="0" step="0.001" data-testid={`supplier-order-qty-${i}`} value={x.quantita}
                    onChange={(e) => setRighe(righe.map((y, j) => (j === i ? { ...y, quantita: e.target.value } : y)))} />
                  <Input className="col-span-3 font-mono" type="number" min="0" step="0.01" data-testid={`supplier-order-cost-${i}`} value={x.costo}
                    onChange={(e) => setRighe(righe.map((y, j) => (j === i ? { ...y, costo: e.target.value } : y)))} />
                </div>
              ))}
            </div>
          )}
          <Field label="Note per il fornitore (facoltative)"><Input data-testid="supplier-order-note-input" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
          <DialogFooter><Button data-testid="supplier-order-print-button" disabled={busy || !fid || righe.length === 0} onClick={print}><Printer className="mr-2 h-4 w-4" />Crea e stampa PDF</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
